import { Inject, Injectable } from '@angular/core';
import {
  CustomData,
  KameleoonClient,
  SDKParameters,
  Variation,
} from '@kameleoon/javascript-sdk';
import {
  ExportToken,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import { KameleoonService, TOKENS } from '@trustedshops/tswp-core-masterdata';

/**
 * Custom data key IDs for Kameleoon visitor data.
 * @see {@link https://app.kameleoon.com/customData/dashboard | Kameleoon Custom Data Dashboard}
 */
const ACCOUNT_ID_KEY = 0;
const SEGMENT_KEY = 1;
const COUNTRY_CODE_KEY = 2;

/**
 * Implementation of the KameleoonService for feature flag management.
 *
 * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags | Feature Flags Documentation}
 */
@Injectable()
@ExportToken(TOKENS.KameleoonService)
export class KameleoonServiceImpl implements KameleoonService {
  private static readonly TYPE = 'KameleoonServiceImpl';

  #client: KameleoonClient;
  #initializedPromise: Promise<void>;
  #environment: string | undefined;
  #hasConsent = false;

  constructor(
    @Inject(COMMON_TOKENS.LogService)
    private readonly logService: LogService
  ) {}

  /**
   * The Kameleoon environment the client was initialized with (e.g. `integr`,
   * `qa`, `production`). `undefined` before {@link initialize} has run.
   */
  public get environment(): string | undefined {
    return this.#environment;
  }

  /**
   * Whether the current visitor has granted consent for Kameleoon. Defaults to
   * `false` until the bootstrapper records the resolved consent via
   * {@link setConsent}.
   */
  public hasConsent(): boolean {
    return this.#hasConsent;
  }

  /**
   * Records whether the current visitor has granted consent for Kameleoon.
   * Called by the platform bootstrapper once consent has been resolved.
   *
   * @param hasConsent - `true` when consent was granted, `false` otherwise
   */
  public setConsent(hasConsent: boolean): void {
    this.#hasConsent = hasConsent;
  }

  /**
   * Initializes the Kameleoon client and associates the visitor with custom data.
   *
   * @param clientParameters - SDK configuration parameters
   * @param accountId - Account ID for visitor segmentation (Custom Data key `0`)
   * @param countryCode - Optional channel target market (Custom Data key `2`)
   * @param segment - Optional customer segment (Custom Data key `1`)
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#service-initialization | Feature Flags Documentation — Service Initialization}
   */
  public async initialize(
    clientParameters: SDKParameters,
    accountId: string,
    countryCode?: string,
    segment?: string
  ): Promise<void> {
    this.#client = new KameleoonClient(clientParameters);
    this.#environment = clientParameters.configuration?.environment;

    this.#initializedPromise = this.#client
      .initialize()
      .then((result) => {
        this.logService.debug(
          KameleoonServiceImpl.TYPE,
          'Kameleoon initialized successfully:',
          result
        );

        const visitorCode = this.#client.getVisitorCode();
        this.#client.addData(
          visitorCode,
          new CustomData(ACCOUNT_ID_KEY, accountId)
        );

        const trimmedSegment = segment?.trim();
        if (trimmedSegment) {
          this.#client.addData(
            visitorCode,
            new CustomData(SEGMENT_KEY, trimmedSegment)
          );
        }

        const trimmedCountryCode = countryCode?.trim();
        if (trimmedCountryCode) {
          this.#client.addData(
            visitorCode,
            new CustomData(COUNTRY_CODE_KEY, trimmedCountryCode)
          );
        }
      })
      .catch((error) => {
        this.logService.error(
          KameleoonServiceImpl.TYPE,
          new Error('Kameleoon init failed'),
          error
        );
        throw error;
      });

    return this.#initializedPromise;
  }

  /**
   * Updates the visitor's country Custom Data (key `2`) with the channel target market.
   *
   * @param countryCode - Raw channel `targetMarket` string (e.g. `DEU`, `AT`, `CH`)
   */
  public async setCountryCode(countryCode: string): Promise<void> {
    try {
      const trimmedCountryCode = countryCode?.trim();
      if (!trimmedCountryCode) {
        return;
      }

      const client = await this.#getReadyClient('country code update');
      if (!client) {
        return;
      }

      const visitorCode = client.getVisitorCode();
      client.addData(
        visitorCode,
        new CustomData(COUNTRY_CODE_KEY, trimmedCountryCode)
      );
    } catch (error) {
      this.logService.debug(
        KameleoonServiceImpl.TYPE,
        'Error setting country code custom data',
        error
      );
    }
  }

  /**
   * Checks if a feature flag is active for the current visitor.
   * Returns `false` if not initialized or on error.
   *
   * @param featureFlagName - Feature flag name from Kameleoon dashboard
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#checking-feature-flags | Feature Flags Documentation — Checking Feature Flags}
   */
  public async isFeatureFlagActive(featureFlagName: string): Promise<boolean> {
    try {
      if (!this.#initializedPromise || !this.#client) {
        this.logService.debug(
          KameleoonServiceImpl.TYPE,
          'Kameleoon client not initialized, returning false for feature flag:',
          featureFlagName
        );
        return false;
      }

      await this.#initializedPromise;

      const visitorCode = this.#client.getVisitorCode();
      return this.#client.isFeatureFlagActive(visitorCode, featureFlagName);
    } catch (error) {
      this.logService.debug(
        KameleoonServiceImpl.TYPE,
        `Error checking feature flag, returning false: ${featureFlagName}`,
        error
      );
      return false;
    }
  }

  /**
   * Returns the variation (experiment result) assigned to the current visitor
   * for the given feature flag, including its `experimentId` and variables.
   * Returns `null` if not initialized or on error.
   *
   * @param featureKey - Feature flag key from the Kameleoon dashboard
   * @param track - Whether to send a tracking request for the evaluation
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#resolving-a-variation | Feature Flags Documentation — Resolving a Variation}
   */
  public async getVariation(
    featureKey: string,
    track = true
  ): Promise<Variation | null> {
    try {
      const client = await this.#getReadyClient(
        `variation for feature flag: ${featureKey}`
      );
      if (!client) {
        return null;
      }

      const visitorCode = client.getVisitorCode();
      return client.getVariation({ visitorCode, featureKey, track });
    } catch (error) {
      this.logService.debug(
        KameleoonServiceImpl.TYPE,
        `Error getting variation, returning null: ${featureKey}`,
        error
      );
      return null;
    }
  }

  /**
   * Returns the variations assigned to the current visitor across all feature
   * flags, keyed by feature flag key. Returns an empty map if not initialized
   * or on error.
   *
   * @param onlyActive - When `true`, only active (non-`off`) variations are returned
   * @param track - Whether to send tracking requests for the evaluations
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#resolving-all-variations | Feature Flags Documentation — Resolving All Variations}
   */
  public async getVariations(
    onlyActive = false,
    track = true
  ): Promise<Map<string, Variation>> {
    try {
      const client = await this.#getReadyClient('variations');
      if (!client) {
        return new Map();
      }

      const visitorCode = client.getVisitorCode();
      return client.getVariations({ visitorCode, onlyActive, track });
    } catch (error) {
      this.logService.debug(
        KameleoonServiceImpl.TYPE,
        'Error getting variations, returning empty map',
        error
      );
      return new Map();
    }
  }

  /**
   * Returns the value of a single variable of the variation assigned to the
   * current visitor for the given feature flag. Returns `defaultValue` if not
   * initialized, the variable is missing, or on error.
   *
   * @param featureKey - Feature flag key from the Kameleoon dashboard
   * @param variableKey - Variable key within the feature flag
   * @param defaultValue - Value returned when the variable cannot be resolved
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#reading-a-single-variable | Feature Flags Documentation — Reading a Single Variable}
   */
  public async getFeatureVariable<T = unknown>(
    featureKey: string,
    variableKey: string,
    defaultValue?: T
  ): Promise<T | undefined> {
    try {
      const variation = await this.getVariation(featureKey);
      const variable = variation?.variables.get(variableKey);

      if (variable === undefined) {
        return defaultValue;
      }

      return variable.value as T;
    } catch (error) {
      this.logService.debug(
        KameleoonServiceImpl.TYPE,
        `Error getting feature variable, returning default value: ${featureKey}.${variableKey}`,
        error
      );
      return defaultValue;
    }
  }

  /**
   * Resolves the initialized Kameleoon client, waiting for initialization to
   * complete. Returns `null` when the client has not been initialized.
   *
   * @param context - Human readable description of the pending operation, used for logging
   */
  async #getReadyClient(context: string): Promise<KameleoonClient | null> {
    if (!this.#initializedPromise || !this.#client) {
      this.logService.debug(
        KameleoonServiceImpl.TYPE,
        `Kameleoon client not initialized, skipping ${context}`
      );
      return null;
    }

    await this.#initializedPromise;
    return this.#client;
  }
}
